import React, { useEffect, useRef, createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { OrderStatus, UserRole, MessageChannel } from '../types';
import { db } from '../services/firebase';
import firebase from 'firebase/compat/app';
import { buildNotificationUrl, buildNotificationTag, type NotificationData } from '../services/notificationRouting';
import { USE_SUPABASE, supabase, msFromISO0, isoFromMs, fetchAllRows } from '../services/supabaseClient';
import { subscribeTableHub } from '../services/tableHub';
import { ConversationMetaCache, computePersistentUnread, META_MAX_AGE_MS, type ChunkResult } from '../services/conversationMetaCache';
import { roleHasChat } from '../services/liveTablePolicy';
import { subscribeThreads, unreadCountFor, type SmsThreadSummary } from '../services/smsThreadsApi';
import { subscribeSharedTextRead, advanceSharedTextRead, sharedTextReadSettled } from '../services/textReadState';
import { subscribeBlockedNumbers } from '../services/contactsApi';
import { normalizePhone, textThreadKey } from '../services/phone';
// WolfChat parity (contract items 5 and 9): group-room messages and reactions
// raise the SAME in-tab alert + sound as an order-chat message, and a reaction
// on MY order-chat / DM message does too. The rules and the single app-wide
// room subscription live in their own files — this component only supplies the
// alert call and the click target.
import { FEATURES } from '../services/features';
import { subscribeBoardDirectory } from '../services/live/boardDirectory';
import { isNotificationMuted, loadNotificationPrefs } from '../services/notificationPrefs';
import { useRoomAlerts, type RoomAlert } from '../services/useRoomAlerts';
import { useRoomMutes } from '../services/roomMuteState';
import { decideReactionAlert } from '../services/reactionAlert';
import { previewSnippet, reactionPreviewBody } from '../services/conversationPreview';
// Closed-tab browser notifications (standard Web Push, no Firebase — Umer,
// 2026-09-16). This component owns the lifecycle (subscribe on sign-in and on
// the permission grant, unsubscribe on sign-out); the mechanics live in the
// service so they can be tested without a React tree.
import { ensureWebPushSubscription, dropWebPushSubscription } from '../services/webPush';
import { subscribeNewInboxEmails } from '../services/emailAlerts';
import { emailDocId } from '../services/emailCache';
import { directPeers } from '../services/chat/directPeers';
import type { EmailRecord } from '../services/emailApi';
// The bell / dropdown panel (components/NotificationCenter) reads from this
// store; every fireNotification below also pushes here so a missed toast is
// still recoverable from the panel. setNotificationUser scopes the log to the
// signed-in account (localStorage, not the DB — this is per-device history).
import { addNotification, setNotificationUser, markChatNotificationsRead, reconcileChatNotifications } from '../services/notificationCenter';
import { isSuperTier } from '../services/roles';

// --- Types ---
interface UnreadInfo {
  orderId: string;
  channel: string;
  channels: string[];       // ALL channels that have unread messages
  channelCounts: Record<string, number>;  // Per-channel unread message counts
  senderName: string;
  lastMessage: string;
  timestamp: number;
  count: number;
}

interface NotificationState {
  unreadCount: number;
  unreadOrders: Record<string, UnreadInfo>;
  // Aggregate unread counts per channel across all orders. Lets upstream
  // badges (sidebar, bell, bottom tabs) show *what kind* of message is
  // unread — in particular, customer SMS replies are called out separately.
  unreadByChannel: { customer: number; store: number; dispatch: number };
  clearUnread: () => void;
  markOrderRead: (orderId: string) => void;
  markChannelRead: (orderId: string, channel: string) => void;
  /** The driver opened this order (from Chat): persist the order-level mark
   *  so the "new delivery" entry cannot come back on the next reload. Does
   *  not touch the per-channel read cursors. */
  markOrderSeen: (orderId: string) => void;
  // Text Inbox (forwarded SIM-phone SMS): count of inbound texts newer than
  // the last time this browser viewed the Text Inbox, and the read marker.
  unreadTexts: number;
  markTextsRead: () => void;
  // Per-conversation read: opening a thread marks only THAT number read, so
  // the tab badge keeps showing the true remaining unread count (1-9, then 9+).
  markTextRead: (number: string, atMs?: number) => void;
  // Merged read marks (server copy on users/{uid}.textReadMarks ∨ this
  // browser's localStorage, max per thread). textMarksVersion bumps whenever
  // either side changes so consumers can re-derive unread counts.
  readTextMarks: () => Record<string, number>;
  // Whole-inbox "read before this" floor, merged server ∨ localStorage.
  readTextFloor: () => number;
  textMarksVersion: number;
  notificationPermission: string;
  requestPermission: () => void;
  flashActive: boolean;
  dismissFlash: () => void;
}

// --- Context ---
/** How long a group-chat alert tints the frame red before clearing itself. */
const ROOM_FLASH_MS = 4000;

const NotificationContext = createContext<NotificationState>({
  unreadCount: 0,
  unreadOrders: {},
  unreadByChannel: { customer: 0, store: 0, dispatch: 0 },
  clearUnread: () => {},
  markOrderRead: () => {},
  markChannelRead: () => {},
  markOrderSeen: () => {},
  unreadTexts: 0,
  markTextsRead: () => {},
  markTextRead: () => {},
  readTextMarks: () => ({}),
  readTextFloor: () => 0,
  textMarksVersion: 0,
  notificationPermission: 'default',
  requestPermission: () => {},
  flashActive: false,
  dismissFlash: () => {},
});
export const useNotifications = () => useContext(NotificationContext);

// --- Sound ---
const playNotificationSound = () => {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.frequency.value = 880;
    oscillator.type = 'sine';
    gain.gain.value = 0.3;
    oscillator.start();
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    oscillator.stop(ctx.currentTime + 0.3);
  } catch (e) { /* silent */ }
};

const vibrateDevice = () => {
  try { if (navigator.vibrate) navigator.vibrate([200, 100, 200]); } catch (e) { /* silent */ }
};

// --- Fire notification (in-tab) ---
// The IN-TAB half of the alert story: sound, vibration and an OS popup for an
// event this tab witnessed live. The closed-tab half is Web Push through
// public/sw.js, and the worker deliberately stays quiet while a window of ours
// is focused so the two never double up. Stable tags mean the OS replaces
// older notifications carrying the same tag, so duplicates collapse to one.
// The in-tab toast for every alert. Registered by the provider (it owns the
// AppContext hook); a plain module function so fireNotification stays callable
// from the listener effects without threading a callback through each one.
let toastSink: ((title: string, body: string, onClick?: () => void) => void) | null = null;

function fireNotification(title: string, body: string, data: NotificationData, onClickFallback?: () => void, url?: string) {
  // Settings → Notifications: a category the user switched off raises nothing —
  // no toast, sound, bell row or OS popup. Unread badges are counted elsewhere
  // and stay, on purpose (services/notificationPrefs.ts).
  if (isNotificationMuted(data?.type)) return;
  // Always a clickable toast in the tab (Umer, 2026-09-16): the title is the
  // source, the body the preview, and tapping it goes where the event lives.
  toastSink?.(title, body, onClickFallback);
  vibrateDevice();
  // Sound on EVERY alert (Umer, 2026-09-17): Chrome on macOS shows the OS
  // popup silently by default, so leaving the tone to the OS meant no sound.
  playNotificationSound();
  // The toast is 5-second ephemeral. The bell/panel is the durable log — every
  // alert also lands there, keyed by the same stable tag OS notifications use
  // for dedupe, so a burst in one chat becomes ONE row (newest body).
  addNotification({ title, body, data, url, tag: buildNotificationTag(data) });

  // The OS notification (macOS Notification Center / Windows Action Center)
  // is shown when the browser allows it; the tone above already played.
  if (!('Notification' in window) || Notification.permission !== 'granted') return;

  try {
    const n = new Notification(title, {
      body,
      icon: 'https://cdn-icons-png.flaticon.com/512/2972/2972185.png',
      tag: buildNotificationTag(data),
    });
    n.onclick = () => {
      window.focus();
      if (onClickFallback) onClickFallback();
      n.close();
    };
    // No auto-close: the stable tag already collapses duplicates, and an
    // ops-critical popup vanishing after 5s reads as "no notification".
  } catch {
    // Android Chrome refuses the Notification constructor without a service
    // worker; the in-app toast + badge (and the tone above) are the alert there.
  }
}

// --- Catch-up after a sign-in (Umer, 2026-10-01) ---
// The first thread / email snapshot after a sign-in is history, not news. A
// fresh machine, a private window or a second shared account used to get one
// toast AND one OS popup per unread thread — up to thirty in a row — because
// that snapshot was judged before the team's read ledger had arrived, and
// nothing was capped. Now: everything still unread gets a bell row (the bell
// lists it all), the newest CATCH_UP_MAX get a toast, the rest fold into one
// summary toast, one tone — and no OS popup at all. Live alerts while the app
// is open are untouched: they still go through fireNotification above.
export const CATCH_UP_MAX = 5;
// How long the first text snapshot waits for the shared read ledger before
// alerting anyway — never blocked for ever if the function is slow or down.
export const CATCH_UP_LEDGER_WAIT_MS = 3000;
export const catchUpSummary = (rest: number, noun: 'text' | 'email'): string =>
  `${rest} more unread ${noun}${rest === 1 ? '' : 's'} since you were away.`;

interface CatchUpItem { title: string; body: string; data: NotificationData; onClick: () => void; url: string }

function fireCatchUp(items: CatchUpItem[], noun: 'text' | 'email', summaryTitle: string, onSummaryClick: () => void) {
  const live = items.filter((i) => !isNotificationMuted(i.data?.type));
  if (live.length === 0) return;
  for (const i of live) addNotification({ title: i.title, body: i.body, data: i.data, url: i.url, tag: buildNotificationTag(i.data) });
  for (const i of live.slice(0, CATCH_UP_MAX)) toastSink?.(i.title, i.body, i.onClick);
  const rest = live.length - CATCH_UP_MAX;
  if (rest > 0) toastSink?.(summaryTitle, catchUpSummary(rest, noun), onSummaryClick);
  vibrateDevice();
  playNotificationSound();
}

// --- Text-Inbox read state, Postgres home (USE_SUPABASE) ---
// Text-Inbox read marks are now a SHARED team ledger (services/textReadState.ts
// → text-read-state function → one Firestore doc), identical in both backends.
// The old per-user user_read_state 'text_*' rows / users.textReadMarks are no
// longer written; a fresh device carries its localStorage floor up on first
// load (see the subscribe effect) so nothing resurfaces.

// --- Provider ---
// The roles that have the Text Inbox (components/Sidebar.tsx) — the shared read ledger is theirs.
const TEXT_INBOX_ROLES: string[] = [UserRole.OWNER, UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DISPATCH];

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, markChatRead, markChatReadBatch, rooms, users, showToast } = useApp();
  // The signed-in user's alert switches — loaded once per user, local copy first.
  useEffect(() => { void loadNotificationPrefs(currentUser?.id, currentUser?.role); }, [currentUser?.id, currentUser?.role]);
  // fireNotification's toast goes through the app's own toast stack (clickable,
  // titled — see components/ToastContainer.tsx).
  useEffect(() => {
    toastSink = (title, body, onClick) => showToast(body, 'info', { title, onClick });
    return () => { toastSink = null; };
  }, [showToast]);

  // Two separate unread sources: persistent (from Firestore metadata) and realtime (from listeners)
  const [persistentUnread, setPersistentUnread] = useState<Record<string, UnreadInfo>>({});
  const [realtimeUnread, setRealtimeUnread] = useState<Record<string, UnreadInfo>>({});
  const [flashActive, setFlashActive] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(
    'Notification' in window ? Notification.permission : 'denied'
  );

  // Merge persistent + realtime into one view
  const unreadOrders: Record<string, UnreadInfo> = useMemo(() => {
    const merged: Record<string, UnreadInfo> = {};
    const pEntries = Object.entries(persistentUnread) as [string, UnreadInfo][];
    for (const [id, info] of pEntries) {
      merged[id] = { ...info };
    }
    const rEntries = Object.entries(realtimeUnread) as [string, UnreadInfo][];
    for (const [id, info] of rEntries) {
      if (merged[id]) {
        const p = merged[id];
        const allChannels = [...new Set([...p.channels, ...info.channels])];
        // Both sources approximate the SAME thing — "messages since this
        // user's lastReadChat" — so SUMMING them double-counted every message
        // both had seen (read the chat, get ONE new text, the row said 2).
        // Per-channel MAX keeps whichever source has the fresher tally.
        const mergedCounts: Record<string, number> = { ...(p.channelCounts || {}) };
        for (const [ch, cnt] of Object.entries(info.channelCounts || {})) {
          mergedCounts[ch] = Math.max(mergedCounts[ch] || 0, cnt);
        }
        const total = Object.values(mergedCounts).reduce((s, n) => s + n, 0);
        merged[id] = {
          ...info,
          channels: allChannels,
          channelCounts: mergedCounts,
          count: total || Math.max(p.count, info.count),
        };
      } else {
        merged[id] = { ...info };
      }
    }
    return merged;
  }, [persistentUnread, realtimeUnread]);

  const flashTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dismissFlash = useCallback(() => {
    if (flashTimerRef.current) { clearTimeout(flashTimerRef.current); flashTimerRef.current = null; }
    setFlashActive(false);
  }, []);
  // The red screen flash is OFF (Umer, 2026-09-18): every alert already raises a
  // toast, a sound, the bell and an OS notification, and the full-screen red
  // border on top of that was too much. The callers keep calling triggerFlash so
  // turning it back on is this one function; it no longer sets flashActive.
  const triggerFlash = useCallback((_opts?: { autoDismissMs?: number }) => {
    if (flashTimerRef.current) { clearTimeout(flashTimerRef.current); flashTimerRef.current = null; }
  }, []);
  useEffect(() => () => { if (flashTimerRef.current) clearTimeout(flashTimerRef.current); }, []);

  const navigate = useNavigate();

  // ── Group-chat alerts (FEATURES.chatParity) ───────────────────────────────
  // One subscription for the whole app, not one per room; muted rooms are
  // silent unless the message @-mentions me, and the room currently on screen
  // in a focused tab never pops.
  const roomAlertsOn = FEATURES.chatParity && USE_SUPABASE && !!currentUser?.id;
  useRoomMutes(roomAlertsOn);
  const onRoomAlert = useCallback((alert: RoomAlert) => {
    const data = { type: 'room_message', roomId: alert.roomId, messageId: alert.messageId };
    const url = buildNotificationUrl(data, currentUser?.role || undefined);
    fireNotification(alert.title, alert.body, data, () => navigate(url), url);
    // A message flashes the frame briefly; a reaction is toast + OS popup only.
    if (alert.kind === 'message') triggerFlash({ autoDismissMs: ROOM_FLASH_MS });
  }, [navigate, triggerFlash, currentUser?.role]);
  useRoomAlerts({
    enabled: roomAlertsOn,
    meId: currentUser?.id,
    rooms: rooms ?? [],
    users: users ?? [],
    onAlert: onRoomAlert,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      if ('Notification' in window) {
        const current = Notification.permission;
        setNotificationPermission(prev => prev !== current ? current : prev);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // ═══ WEB PUSH: subscribe this browser, so a CLOSED tab still alerts ══════
  // Standard Web Push (RFC 8030 + VAPID), no Firebase. Runs when the user is
  // signed in AND has already granted permission — asking is the bell banner's
  // job, and `requestPermission` below subscribes the moment a grant lands so
  // the first push does not have to wait for a reload. Skipped entirely
  // without a public key for this project (services/webPushKeys.ts; every vitest
  // run), and on any browser without a service worker.
  useEffect(() => {
    if (!currentUser?.id) return;
    if (!USE_SUPABASE) return; // subscriptions live in Postgres (migration 0066)
    if (notificationPermission !== 'granted') return;
    void ensureWebPushSubscription(currentUser.id);
  }, [currentUser?.id, notificationPermission]);

  // Sign-out: stop this browser receiving pushes for the account that left.
  // Keyed off the id going from set → unset; the service cancels the browser
  // subscription too, so a row that outlives the session is pruned on its next
  // 410 anyway.
  const lastSignedInRef = useRef<string>('');
  useEffect(() => {
    const id = currentUser?.id || '';
    if (lastSignedInRef.current && !id) void dropWebPushSubscription();
    lastSignedInRef.current = id;
  }, [currentUser?.id]);

  // The in-app notification bell/panel log is per-account (localStorage), so
  // switching users on the same browser must swap the store; signing out
  // clears the in-memory list without touching another user's saved history.
  useEffect(() => {
    setNotificationUser(currentUser?.id || null);
  }, [currentUser?.id]);
  // Chat rows in the bell follow the database read state: a thread read in any tab or on any
  // device (user_read_state → lastReadChat) clears its bell rows here too (2026-09-24).
  useEffect(() => {
    reconcileChatNotifications(currentUser?.lastReadChat);
  }, [currentUser?.id, currentUser?.lastReadChat]);

  // A notification clicked while this tab is open: public/sw.js calls
  // client.navigate() and ALSO posts this, because Safari refuses a
  // worker-driven navigation. Routing through the router keeps the SPA state.
  useEffect(() => {
    // Truthiness, not `in`: a jsdom/test navigator can carry the key with an
    // undefined value, and a missing service worker must be a no-op, never a
    // crash that takes the whole provider (and every other alert) down.
    const sw = typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined;
    if (!sw || typeof sw.addEventListener !== 'function') return;
    const handler = (e: MessageEvent) => {
      if (e.data?.type === 'NAVIGATE' && typeof e.data.url === 'string') navigate(e.data.url);
    };
    sw.addEventListener('message', handler);
    return () => sw.removeEventListener('message', handler);
  }, [navigate]);

  // ═══ TEXT INBOX (forwarded SIM-phone SMS): app-wide alerts ═══════════════
  // The Text Inbox page only listens while it is mounted, so a text that
  // landed while the dispatcher sat on any other screen made no sound and
  // showed no badge. This provider-level listener subscribes to the same
  // `smsThreads` summaries from EVERY screen (admin-tier roles only —
  // firestore.rules denies everyone else) and fires a browser notification +
  // sidebar badge for new inbound texts. Live, not polled: one read per
  // change instead of a full list every 20 s.
  const location = useLocation();
  const [unreadTexts, setUnreadTexts] = useState(0);
  const onTextInboxRef = useRef(false);
  onTextInboxRef.current = location.pathname.startsWith('/text_inbox');
  // H2: live set of BLOCKED numbers (normalizePhone keys) — texts from these
  // never badge or notify. The poller reads the ref so it always sees the
  // current set without re-subscribing.
  const blockedRef = useRef<Set<string>>(new Set());

  const textReadKey = currentUser?.id ? `wolf_textInboxReadAt_${currentUser.id}` : '';
  // Per-thread read marks: { [normalized number]: lastReadMs }. The legacy
  // whole-inbox timestamp above stays as a floor so threads read before this
  // model existed don't all flip back to unread.
  const textMarksKey = currentUser?.id ? `wolf_textReadMarks_${currentUser.id}` : '';
  const lastTextThreadsRef = useRef<SmsThreadSummary[]>([]);

  // Server copy of the read marks, kept on the user's OWN users/{uid} doc
  // (field `textReadMarks`) — self-updates are already allowed by the deployed
  // rules on both environments, so this needs NO rules change. localStorage
  // alone made unread counts per-browser: any fresh machine/profile showed
  // every historical inbound text as unread (the "5 unread but nothing new"
  // report), and reading on one device never cleared the other.
  const serverTextMarksRef = useRef<Record<string, number>>({});
  // Server copy of the whole-inbox floor ("everything before this is read").
  // The floor predates per-thread marks and lived only in localStorage — the
  // one piece of read-state that was still per-browser, so a fresh device
  // showed years-old threads as unread until each was opened once. The floor
  // now syncs through users/{uid}.textReadFloor: read state belongs to the
  // ACCOUNT, whatever browser or device it signs in from.
  const serverTextFloorRef = useRef(0);
  const [textMarksVersion, setTextMarksVersion] = useState(0);

  const readTextFloor = useCallback((): number => {
    let local = 0;
    if (textReadKey) {
      try { local = Number(localStorage.getItem(textReadKey)) || 0; } catch { /* private mode */ }
    }
    return Math.max(local, serverTextFloorRef.current);
  }, [textReadKey]);

  const readTextMarks = useCallback((): Record<string, number> => {
    let local: Record<string, number> = {};
    if (textMarksKey) {
      try { local = JSON.parse(localStorage.getItem(textMarksKey) || '{}') || {}; } catch { /* private mode */ }
    }
    const merged: Record<string, number> = { ...serverTextMarksRef.current };
    for (const k of Object.keys(local)) merged[k] = Math.max(merged[k] || 0, local[k]);
    return merged;
  }, [textMarksKey]);

  // The badge needs BOTH halves: the threads and the team's shared read ledger.
  // Threads paint from cache instantly on a reload; the ledger comes from the
  // server a moment later. Counting in between flashed a badge for every thread
  // a colleague (or another device) had already read, on every reload (staging,
  // 2026-09-21). So: start from the last ledger this browser saw, and show no
  // count at all until some ledger — cached or fresh — is known.
  const sharedReadKnownRef = useRef(false);
  const recountUnreadTexts = useCallback(() => {
    if (!sharedReadKnownRef.current) return;
    const floor = readTextFloor();
    const marks = readTextMarks();
    // CONVERSATION count, not message count (per Umer, 2026-08-28): six
    // unread texts from one number badge the sidebar as 1. The inbox rows
    // keep their own per-thread message counts.
    let n = 0;
    for (const t of lastTextThreadsRef.current) {
      if (unreadCountFor(t, Math.max(marks[t.threadKey] || 0, floor)) > 0) n += 1;
    }
    setUnreadTexts(n);
  }, [readTextFloor, readTextMarks]);

  const markTextRead = useCallback((number: string, atMs?: number) => {
    if (!textMarksKey || !currentUser?.id) return;
    const key = textThreadKey(number);
    if (!key) return;
    // Never move a mark backwards; default to wall clock only when the
    // caller has no rendered-message timestamp to anchor to.
    const next = Math.max(readTextMarks()[key] || 0, atMs ?? Date.now());
    try {
      const local = JSON.parse(localStorage.getItem(textMarksKey) || '{}') || {};
      local[key] = Math.max(local[key] || 0, next);
      localStorage.setItem(textMarksKey, JSON.stringify(local));
    } catch { /* private mode */ }
    // Mirror to the server so every browser/device this user signs into
    // agrees. Optimistic ref update first: the inbox re-marks on every poll
    // while a thread is open, and only an actual advance should write.
    if (next > (serverTextMarksRef.current[key] || 0)) {
      serverTextMarksRef.current = { ...serverTextMarksRef.current, [key]: next };
      setTextMarksVersion(v => v + 1);
      // SHARED across the whole dispatch team (both backends): one dispatcher
      // reading a thread clears it for everyone — the read ledger is one
      // Firestore doc behind text-read-state, not a per-user row.
      advanceSharedTextRead({ marks: { [key]: next } });
    }
    recountUnreadTexts();
  }, [textMarksKey, currentUser?.id, readTextMarks, recountUnreadTexts]);

  // SHARED team read ledger: a thread read by ANY dispatcher (on any machine,
  // in either backend) clears here too. One Firestore doc behind
  // text-read-state, polled + pushed-on-write — no per-user state any more.
  // Only the Text Inbox roles share this ledger (Phase 1, 2026-09-30): every driver and store tab called
  // text-read-state every 15 s all day and was refused — the function answers dispatch roles only.
  const hasTextInbox = !!currentUser && TEXT_INBOX_ROLES.includes(currentUser.role);
  useEffect(() => {
    if (!currentUser?.id || !hasTextInbox) return;
    // One-time carry-up: a browser that bulk-read the inbox under the old
    // localStorage-only model lifts that floor into the shared ledger, so the
    // team agrees instead of resurfacing ancient threads on a fresh device.
    let localFloor = 0;
    try { localFloor = Number(localStorage.getItem(textReadKey)) || 0; } catch { /* private mode */ }
    if (localFloor > 0) advanceSharedTextRead({ floor: localFloor });

    // Last ledger this browser saw for this account → the first paint is right.
    const sharedCacheKey = `textSharedRead.v1:${currentUser.id}`;
    try {
      const cached = JSON.parse(localStorage.getItem(sharedCacheKey) || 'null');
      if (cached && typeof cached === 'object') {
        serverTextMarksRef.current = { ...(cached.marks || {}), ...serverTextMarksRef.current };
        serverTextFloorRef.current = Math.max(serverTextFloorRef.current, Number(cached.floor) || 0);
        sharedReadKnownRef.current = true;
        recountUnreadTexts();
      }
    } catch { /* private mode / malformed — wait for the server */ }

    const unsub = subscribeSharedTextRead((shared) => {
      // Keep any optimistic local advances the poll hasn't caught up to yet.
      const merged: Record<string, number> = { ...shared.marks };
      for (const k of Object.keys(serverTextMarksRef.current)) {
        merged[k] = Math.max(merged[k] || 0, serverTextMarksRef.current[k]);
      }
      serverTextMarksRef.current = merged;
      serverTextFloorRef.current = Math.max(serverTextFloorRef.current, shared.floor);
      // The subscription fires at once with its in-memory value, which on a fresh
      // load is the EMPTY default — that is not knowledge. Wait for the first
      // fetch to settle (or for the cached ledger above).
      if (!sharedTextReadSettled()) return;
      sharedReadKnownRef.current = true;
      try {
        localStorage.setItem(sharedCacheKey, JSON.stringify({ marks: serverTextMarksRef.current, floor: serverTextFloorRef.current }));
      } catch { /* private mode */ }
      setTextMarksVersion(v => v + 1);
      recountUnreadTexts();
    });
    return () => { unsub(); sharedReadKnownRef.current = false; };
  }, [currentUser?.id, hasTextInbox, recountUnreadTexts, textReadKey]);

  // "Mark everything read" — kept for completeness; nothing calls it on tab
  // open any more (per Umer the badge must show the real count at all times).
  const markTextsRead = useCallback(() => {
    const now = Date.now();
    if (textReadKey) {
      try { localStorage.setItem(textReadKey, String(now)); } catch { /* private mode */ }
    }
    serverTextFloorRef.current = Math.max(serverTextFloorRef.current, now);
    advanceSharedTextRead({ floor: now }); // shared team floor (both backends)
    setUnreadTexts(0);
  }, [textReadKey, currentUser?.id]);

  // H2: keep the blocked-number set live. Only admin-tier roles may read
  // smsContacts, so gate the subscription by role (drivers/stores would 403).
  useEffect(() => {
    const role = currentUser?.role;
    const allowed = isSuperTier(role) || role === UserRole.ADMIN || role === UserRole.DISPATCH;
    if (!allowed) { blockedRef.current = new Set(); return; }
    const off = subscribeBlockedNumbers((s) => { blockedRef.current = s; });
    return off;
  }, [currentUser?.role]);

  useEffect(() => {
    const role = currentUser?.role;
    const allowed = isSuperTier(role) || role === UserRole.ADMIN || role === UserRole.DISPATCH;
    if (!allowed || !currentUser?.id || !textReadKey) {
      setUnreadTexts(0);
      return;
    }
    const seesOtp = isSuperTier(role) || role === UserRole.ADMIN;

    // Novelty = unseen inbound MESSAGE ID, not a timestamp watermark. A single
    // wrong-clock stamp used to pin the old max-receivedAt watermark and
    // silence every popup fleet-wide while the badge kept counting — exactly
    // the "message arrived, no notification" report. The seen-set is
    // persisted per user so a reload doesn't re-baseline and swallow
    // everything that arrived while the tab was closed.
    const seenKey = `wolf_textAlertSeen_${currentUser.id}`;
    const seen = new Set<string>((() => {
      try { return JSON.parse(localStorage.getItem(seenKey) || '[]') || []; } catch { return []; }
    })());
    const persistSeen = () => {
      try { localStorage.setItem(seenKey, JSON.stringify([...seen].slice(-800))); } catch { /* private mode */ }
    };
    let firstSnapshot = true;

    const onThreads = (threads: SmsThreadSummary[]) => {
      // H2: a number tagged "Blocked" must not badge or notify (it stays
      // readable in the inbox, just silent). Drop them before any counting.
      const live = threads.filter(t => !blockedRef.current.has(normalizePhone(t.phone)));
      // The badge always shows the true unread count across conversations —
      // being on the tab no longer clears it; opening each thread does
      // (the inbox page calls markTextRead per conversation).
      lastTextThreadsRef.current = live;
      recountUnreadTexts();
      // "Viewing" requires FOCUS, not just visibility — an unattended second
      // monitor with the inbox open must not swallow alerts.
      const viewing = onTextInboxRef.current && document.visibilityState === 'visible' && document.hasFocus();

      const fresh = live.filter(t => t.lastInboundId && !seen.has(t.lastInboundId));
      // After a reload/boot, alert only what is still unread per the read
      // marks AND recent — texts that arrived while no tab was alive are
      // news; last week's history is not.
      let toAlert = fresh;
      const catchUp = firstSnapshot;
      if (firstSnapshot) {
        firstSnapshot = false;
        // Judged against the team's read ledger (the gate below waited for
        // it): a thread a colleague — or this account on another machine —
        // already read is marked seen here and never alerts.
        const floor = readTextFloor();
        const marks = readTextMarks();
        const DAY_MS = 24 * 60 * 60 * 1000;
        toAlert = fresh.filter(t =>
          t.lastInboundAt > Math.max(marks[t.threadKey] || 0, floor) &&
          Date.now() - t.lastInboundAt < DAY_MS
        );
      }
      if (fresh.length) {
        fresh.forEach(t => seen.add(t.lastInboundId!));
        persistSeen();
      }
      if (toAlert.length === 0) return;
      // Actively reading the inbox (focused) — the open page IS the alert.
      if (viewing) return;

      const describe = (t: SmsThreadSummary) => {
        const text = t.lastDirection === 'in' ? t.lastBody : 'New text message';
        const body = text.length > 140 ? `${text.slice(0, 137)}…` : text;
        const textData = { type: 'forwarded_sms', conversationId: t.phone } as const;
        const textUrl = buildNotificationUrl(textData, currentUser?.role || undefined);
        return { title: `📱 Text · ${t.phone}`, body, data: textData, onClick: () => navigate(textUrl), url: textUrl };
      };
      if (catchUp) {
        // What was waiting at sign-in: newest first, five toasts + a summary, no OS popups.
        const newest = [...toAlert].sort((a, b) => b.lastInboundAt - a.lastInboundAt);
        fireCatchUp(newest.map(describe), 'text', '📱 Text Inbox', () => navigate('/text_inbox'));
        triggerFlash();
        return;
      }

      // One notification per conversation: the stable fsms-<number> tag makes
      // a newer text from the same number replace its older popup.
      for (const t of toAlert) {
        const d = describe(t);
        fireNotification(d.title, d.body, d.data, d.onClick, d.url);
      }
      triggerFlash();
    };

    // The gate: the first snapshot is held until the shared read ledger has
    // answered (or CATCH_UP_LEDGER_WAIT_MS passes), so "unread" means unread
    // for the team, not "this browser has no memory of it". Threads keep
    // flowing to the badge meanwhile (the recount itself waits for a known
    // ledger); the newest snapshot is the one judged when the gate opens.
    let pendingFirst: SmsThreadSummary[] | null = null;
    let gateTimer: ReturnType<typeof setTimeout> | null = null;
    let gatePoll: ReturnType<typeof setInterval> | null = null;
    const closeGate = () => {
      if (gateTimer) clearTimeout(gateTimer);
      if (gatePoll) clearInterval(gatePoll);
      gateTimer = null; gatePoll = null;
    };
    const openGate = () => {
      closeGate();
      const held = pendingFirst;
      pendingFirst = null;
      if (held) onThreads(held);
    };
    const off = subscribeThreads(seesOtp, (threads) => {
      if (firstSnapshot && !sharedTextReadSettled()) {
        lastTextThreadsRef.current = threads.filter(t => !blockedRef.current.has(normalizePhone(t.phone)));
        recountUnreadTexts();
        pendingFirst = threads;
        if (!gateTimer) {
          gatePoll = setInterval(() => { if (sharedTextReadSettled()) openGate(); }, 100);
          gateTimer = setTimeout(openGate, CATCH_UP_LEDGER_WAIT_MS);
        }
        return;
      }
      onThreads(threads);
    });
    return () => { closeGate(); off(); };
  }, [currentUser?.id, currentUser?.role, textReadKey, readTextMarks, readTextFloor, recountUnreadTexts, navigate, triggerFlash]);

  // --- New EMAIL alerts (Umer, 2026-09-16) — the Text Inbox pattern, for the
  // inbox. Novelty = unseen cached email id, persisted per user so a reload
  // doesn't re-announce everything; after a boot only unread mail from the
  // last day counts as news. "Viewing" needs focus, not just visibility.
  const onEmailInboxRef = useRef(false);
  onEmailInboxRef.current = location.pathname.startsWith('/inbox');
  useEffect(() => {
    const role = currentUser?.role;
    const allowed =
      FEATURES.emailInbox &&
      (isSuperTier(role) || role === UserRole.ADMIN || role === UserRole.DISPATCH);
    if (!allowed || !currentUser?.id) return;
    const seenKey = `wolf_emailAlertSeen_${currentUser.id}`;
    const seen = new Set<string>((() => {
      try { return JSON.parse(localStorage.getItem(seenKey) || '[]') || []; } catch { return []; }
    })());
    const persistSeen = () => {
      try { localStorage.setItem(seenKey, JSON.stringify([...seen].slice(-800))); } catch { /* private mode */ }
    };
    let firstSnapshot = true;
    const DAY_MS = 24 * 60 * 60 * 1000;
    const MAX_POPUPS = 5; // a big sync batch gets five popups + the flash, not fifty

    return subscribeNewInboxEmails({
      isOnInboxPage: () => onEmailInboxRef.current,
      onRows: (rows) => {
        const viewing = onEmailInboxRef.current && document.visibilityState === 'visible' && document.hasFocus();
        const fresh = rows.filter((r) => !r.read && !seen.has(emailDocId(r.folder, r.uid)));
        let toAlert = fresh;
        const catchUp = firstSnapshot;
        if (firstSnapshot) {
          firstSnapshot = false;
          toAlert = fresh.filter((r) => Date.now() - r.date < DAY_MS);
        }
        if (fresh.length) {
          fresh.forEach((r) => seen.add(emailDocId(r.folder, r.uid)));
          persistSeen();
        }
        if (toAlert.length === 0) return;
        // On the Email page with focus: the list is the alert, but the sound
        // still plays (Umer, 2026-09-16) — no OS popup, no screen flash.
        if (viewing) { playNotificationSound(); return; }
        const describe = (r: EmailRecord): CatchUpItem => {
          const id = emailDocId(r.folder, r.uid);
          const who = r.fromName || r.from || 'Unknown sender';
          const line = r.subject ? `${r.subject}${r.snippet ? ` — ${r.snippet}` : ''}` : r.snippet || 'New email';
          const body = line.length > 140 ? `${line.slice(0, 137)}…` : line;
          const emailUrl = `/inbox/${id}`;
          return { title: `✉️ Email · ${who}`, body, data: { type: 'email', conversationId: id }, onClick: () => navigate(emailUrl), url: emailUrl };
        };
        if (catchUp) {
          // Mail that was waiting at sign-in: the same catch-up rules as texts.
          const newest = [...toAlert].sort((a, b) => b.date - a.date);
          fireCatchUp(newest.map(describe), 'email', '✉️ Email', () => navigate('/inbox'));
          triggerFlash();
          return;
        }
        for (const r of toAlert.slice(0, MAX_POPUPS)) {
          const d = describe(r);
          fireNotification(d.title, d.body, d.data, d.onClick, d.url);
        }
        triggerFlash();
      },
    });
  }, [currentUser?.id, currentUser?.role, navigate, triggerFlash]);

  // CONVERSATION count, not message count (per Umer, 2026-08-28): three
  // unread messages from one chat badge the sidebar / bottom tabs as 1.
  // Only the aggregate badges use this — each chat row keeps its own
  // per-message count via unreadOrders[id].count.
  const unreadCount = Object.keys(unreadOrders).length;

  const unreadByChannel = useMemo(() => {
    const totals = { customer: 0, store: 0, dispatch: 0 };
    for (const info of Object.values(unreadOrders)) {
      const counts = info.channelCounts || {};
      totals.customer += counts.customer || 0;
      totals.store += counts.store || 0;
      totals.dispatch += counts.dispatch || 0;
    }
    return totals;
  }, [unreadOrders]);

  const clearUnread = useCallback(() => {
    setPersistentUnread({});
    setRealtimeUnread({});
  }, []);

  // markOrderRead: clear from both states + persist to Firestore
  const markOrderRead = useCallback((orderId: string) => {
    setPersistentUnread(prev => {
      const next = { ...prev };
      delete next[orderId];
      return next;
    });
    setRealtimeUnread(prev => {
      const next = { ...prev };
      delete next[orderId];
      return next;
    });
    // Single Firestore write for all channels — prevents flicker where
    // the persistent unread effect re-runs between individual writes
    // and briefly re-adds badges for channels not yet written.
    markChatReadBatch(orderId, ['dispatch', 'admin_store', 'driver_store', 'customer'] as MessageChannel[]);
    markChatNotificationsRead(orderId); // its bell rows too (order/board chat or direct chat id)
  }, [markChatReadBatch]);

  // markOrderSeen: the order-level "opened" mark only (user_read_state row
  // with channel ''), the same row the driver app writes when a card is
  // tapped. Without it, opening an order from the Chat page cleared the
  // in-memory "new delivery" entry but nothing recorded that the driver had
  // seen the order, so the next reload rebuilt the entry from the orders list
  // (a stale red "1" tagged DISPATCH that no tab could clear, 2026-09-22).
  const markOrderSeen = useCallback((orderId: string) => {
    if (!currentUser || !USE_SUPABASE) return;
    void supabase().from('user_read_state')
      .upsert({ user_id: currentUser.id, conversation_id: orderId, channel: '', last_read_at: isoFromMs(Date.now()) }, { onConflict: 'user_id,conversation_id,channel' })
      .then(({ error }) => { if (error) console.error('[NotificationHandler] markOrderSeen:', error.message); });
  }, [currentUser?.id]);

  // markChannelRead: clear channel from both states + persist to Firestore
  const markChannelRead = useCallback((orderId: string, channel: string) => {
    const removeChannel = (prev: Record<string, UnreadInfo>) => {
      const existing = prev[orderId];
      if (!existing) return prev;
      const remaining = existing.channels.filter(c => c !== channel);
      const updatedCounts = { ...(existing.channelCounts || {}) };
      delete updatedCounts[channel];
      if (remaining.length === 0) {
        const next = { ...prev };
        delete next[orderId];
        return next;
      }
      return { ...prev, [orderId]: { ...existing, channels: remaining, channelCounts: updatedCounts } };
    };
    setPersistentUnread(removeChannel);
    setRealtimeUnread(removeChannel);
    // Persist to Firestore
    if (['dispatch', 'admin_store', 'driver_store', 'customer'].includes(channel)) {
      markChatRead(orderId, channel as MessageChannel);
    }
    markChatNotificationsRead(orderId, channel); // that channel's bell rows too
  }, [markChatRead]);

  const addUnread = useCallback((info: Omit<UnreadInfo, 'count' | 'channels' | 'channelCounts'>) => {
    setRealtimeUnread(prev => {
      const existing = prev[info.orderId];
      const existingChannels = existing?.channels || [];
      const existingCounts = existing?.channelCounts || {};
      const newChannels = info.channel && !existingChannels.includes(info.channel)
        ? [...existingChannels, info.channel]
        : existingChannels.length > 0 ? existingChannels : (info.channel ? [info.channel] : []);
      const newCounts = info.channel
        ? { ...existingCounts, [info.channel]: (existingCounts[info.channel] || 0) + 1 }
        : existingCounts;
      return {
        ...prev,
        [info.orderId]: {
          ...info,
          channels: newChannels,
          channelCounts: newCounts,
          count: (existing?.count || 0) + 1,
        },
      };
    });
  }, []);

  const setPersistentFromMetadata = useCallback((unread: Record<string, UnreadInfo>) => {
    setPersistentUnread(unread);
  }, []);

  const requestPermission = useCallback(() => {
    if ('Notification' in window && Notification.permission !== 'granted') {
      Notification.requestPermission().then(perm => {
        setNotificationPermission(perm);
        // Subscribe immediately rather than waiting for the effect above to
        // see the state change: the grant is the one moment the browser is
        // guaranteed to allow pushManager.subscribe without a gesture.
        if (perm === 'granted' && USE_SUPABASE && currentUser?.id) {
          void ensureWebPushSubscription(currentUser.id);
        }
      });
    }
  }, [currentUser?.id]);

  return (
    <NotificationContext.Provider value={{ unreadCount, unreadOrders, unreadByChannel, clearUnread, markOrderRead, markChannelRead, markOrderSeen, unreadTexts, markTextsRead, markTextRead, readTextMarks, readTextFloor, textMarksVersion, notificationPermission, requestPermission, flashActive, dismissFlash }}>
      <NotificationHandler addUnread={addUnread} triggerFlash={triggerFlash} setPersistentFromMetadata={setPersistentFromMetadata} />
      {children}
    </NotificationContext.Provider>
  );
};

// --- Handler ---
const NotificationHandler: React.FC<{
  addUnread: (info: Omit<UnreadInfo, 'count' | 'channels'>) => void;
  triggerFlash: () => void;
  setPersistentFromMetadata: (unread: Record<string, UnreadInfo>) => void;
}> = ({ addUnread, triggerFlash, setPersistentFromMetadata }) => {
  const { currentUser, orders, users } = useApp();
  // In-tab notification click fallback (new Notification onclick) deep-links
  // via this. Without it the fallback throws a ReferenceError on click.
  const navigate = useNavigate();
  const knownOrderIdsRef = useRef<Set<string> | null>(null);
  /** An assignment older than this never becomes an in-tab "new delivery". */
  const NEW_DELIVERY_MAX_AGE_MS = 10 * 60 * 1000;
  const messageUnsubRef = useRef<(() => void) | null>(null);
  const startTimeRef = useRef<number>(Date.now() - 5000);
  const persistentLoadedRef = useRef<string>('');
  // The persistent-unread meta cache (services/conversationMetaCache) and the inputs of the latest
  // load, so a fetch that completes after a newer run publishes for the newest read state.
  const metaCacheRef = useRef<ConversationMetaCache>(null as unknown as ConversationMetaCache);
  if (!metaCacheRef.current) {
    metaCacheRef.current = new ConversationMetaCache(
      (ids) => supabase().from('conversations').select('id, meta').in('id', ids) as unknown as Promise<ChunkResult>,
      () => Date.now(),
      Math.round(META_MAX_AGE_MS * (0.8 + Math.random() * 0.4)), // per-tab jitter: tabs never refresh together
    );
  }
  const persistentInputsRef = useRef<{ ids: string[]; lastReadChat: Record<string, Record<string, number>>; viewer: { id: string; role?: string } } | null>(null);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const currentUserIdRef = useRef<string>('');
  const currentUserRoleRef = useRef<string>('');
  useEffect(() => {
    currentUserIdRef.current = currentUser?.id || '';
    currentUserRoleRef.current = currentUser?.role || '';
  }, [currentUser?.id, currentUser?.role]);

  // Fix B: Stable order IDs — useMemo recomputes on orders change, but
  // returns same string value when IDs haven't changed. React compares
  // string deps by value, so downstream effects won't re-trigger.
  const myOrderIdsKey = useMemo(() => {
    if (!currentUser) return '';
    const isAdminOrDispatch = isSuperTier(currentUser.role) || currentUser.role === UserRole.ADMIN || currentUser.role === UserRole.DISPATCH;
    const limit = isAdminOrDispatch ? 50 : 15;
    let ids: string[];
    if (currentUser.role === UserRole.DRIVER) {
      ids = orders.filter(o => o.driverId === currentUser.id).map(o => o.id);
    } else if (currentUser.role === UserRole.STORE) {
      ids = orders.filter(o => o.storeId === currentUser.id).map(o => o.id);
    } else {
      const active = orders.filter(o => o.status !== OrderStatus.DELIVERED);
      if (USE_SUPABASE) {
        // A delivered order keeps chatting — the driver's "ok / fixed" after
        // the drop, a customer follow-up — and admins were never badged for
        // it (the order stayed out of scope, so no unread, no sort-to-top).
        // The Postgres meta load is ONE `in` query with no batching cost, so
        // watch every order the app already holds: active first, then the
        // recently delivered ones (the orders window is active + 30 days).
        const delivered = orders
          .filter(o => o.status === OrderStatus.DELIVERED)
          .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        return [...active, ...delivered].map(o => o.id).join(',');
      }
      ids = active.map(o => o.id);
    }
    return ids.slice(0, limit).join(',');
  }, [orders, currentUser?.id, currentUser?.role]);

  // Stable direct chat IDs — only changes when user list membership changes
  const directIdsKey = useMemo(() => {
    if (!currentUser?.id) return '';
    return directPeers(users, currentUser.id, currentUser.role) // two-way since 2026-10-12 (the rest of the team is not offered drivers). A driver: the office, stores and drivers only (client, 2026-10-12)
      .map(u => `direct_${[currentUser.id, u.id].sort().join('_')}`)
      // 50 matches the mobile app's monitored breadth. At 15, whichever users
      // happened to sort past the cut-off simply never badged — a driver got
      // no unread for a dispatcher outside the first 15 (prod report, Aug 27).
      .slice(0, 50)
      .join(',');
  }, [users, currentUser?.id, currentUser?.role]);

  // Order-Board text threads (conversations/card_<id>) — enumerated by doc-id
  // prefix, exactly how the Messages page lists them. They live OUTSIDE the
  // order/direct id universe above, so until now a message landing in one
  // could never badge anyone (staff only noticed by re-opening the thread).
  // Admin-tier only: the board is a dispatch/admin tool.
  const [cardIdsKey, setCardIdsKey] = useState('');
  useEffect(() => {
    const role = currentUser?.role;
    const adminTier = isSuperTier(role) || role === UserRole.ADMIN || role === UserRole.DISPATCH;
    if (USE_SUPABASE) {
      if (!adminTier) {
        setCardIdsKey('');
        return;
      }
      // Postgres twin of the doc-id prefix range below. Enumerate the BOARD's
      // cards (payment_board, newest first) and derive card_<id> from them,
      // rather than every card_ conversation row: a deleted or re-created
      // card leaves an orphan card_ thread behind, and the Messages page
      // hides those (B18) — badging them here meant "2 unread" with nothing
      // unread to click. Kept live via a debounced re-fetch on board changes.
      if (FEATURES.liveV2Board) {
        // Live v2: no whole-table binding on payment_board at app start. The
        // same id set (every card, newest first) comes from the thin list this
        // tab shares with CustomerReplyAlerts and the Messages page.
        return subscribeBoardDirectory(role, (cards) =>
          setCardIdsKey(cards.map((c) => `card_${c.id}`).join(',')));
      }
      let dead = false;
      let timer: ReturnType<typeof setTimeout> | null = null;
      const fetchNow = async () => {
        // EVERY card, paged past PostgREST's 1000-row cap — the Firestore twin
        // below enumerates the whole card_ prefix, so no card may go unbadged
        // here either (was .limit(50): only the newest 50 cards badged).
        let data: any[];
        try {
          data = await fetchAllRows(() => supabase()
            .from('payment_board')
            .select('id')
            .order('placed_at', { ascending: false }));
        } catch { return; } // transient — board threads just don't badge this session
        if (dead) return;
        setCardIdsKey((data || []).map((r: any) => `card_${r.id as string}`).join(','));
      };
      const schedule = () => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(fetchNow, 250); // coalesce bursts, keep the set live
      };
      fetchNow();
      // The tab's shared `payment_board` binding (services/tableHub).
      const leave = subscribeTableHub('payment_board', { onChange: schedule });
      return () => {
        dead = true;
        if (timer) clearTimeout(timer);
        leave();
      };
    }
    if (!adminTier || !db || typeof (db as { collection?: unknown })?.collection !== 'function') {
      setCardIdsKey('');
      return;
    }
    const unsub = db.collection('conversations')
      .where(firebase.firestore.FieldPath.documentId(), '>=', 'card_')
      .where(firebase.firestore.FieldPath.documentId(), '<=', 'card_')
      .onSnapshot(
        snap => setCardIdsKey(snap.docs.map(d => d.id).slice(0, 50).join(',')),
        () => { /* transient — board threads just don't badge this session */ },
      );
    return () => unsub();
  }, [currentUser?.role]);

  // ═══ PERSISTENT UNREAD: Load from conversation metadata on app load ═══
  // Checks BOTH order chats and direct chats for unread messages.
  useEffect(() => {
    if (USE_SUPABASE) {
      if (!currentUser?.id) return;

      const orderIds = myOrderIdsKey ? myOrderIdsKey.split(',') : [];
      const directIds = directIdsKey ? directIdsKey.split(',') : [];
      const cardIds = cardIdsKey ? cardIdsKey.split(',') : [];
      const allConvIds = [...orderIds, ...directIds, ...cardIds];

      if (allConvIds.length === 0) {
        setPersistentFromMetadata({});
        return;
      }

      const lastReadChat = currentUser.lastReadChat || {};
      const loadKey = `${currentUser.id}_${JSON.stringify(lastReadChat)}_${myOrderIdsKey}_${directIdsKey}_${cardIdsKey}`;
      if (loadKey === persistentLoadedRef.current) return;
      persistentLoadedRef.current = loadKey;

      // conversations.meta is the faithful Firestore conversation doc (lastMessagePerChannel
      // etc., epoch-ms numbers) — same unread logic (services/conversationMetaCache). The meta is
      // CACHED (2026-09-24): this effect re-runs on every read-state change, and re-downloading
      // every watched thread each time was ~40 % of all API traffic. Now a read recomputes from
      // memory; only unseen ids, threads the live feed saw change, and entries past the safety
      // age are fetched (chunked — the URL limit, 2026-09-10).
      const cache = metaCacheRef.current;
      cache.forUser(currentUser.id);
      cache.prune(new Set(allConvIds));
      persistentInputsRef.current = { ids: allConvIds, lastReadChat, viewer: { id: currentUser.id, role: currentUser.role } };
      const publishLatest = () => {
        const cur = persistentInputsRef.current;
        // Until every watched id has meta, keep the badges we have (as the old load did).
        if (!cur || !cache.covers(cur.ids)) return;
        setPersistentFromMetadata(computePersistentUnread(cache.rows(cur.ids), cur.lastReadChat, cur.viewer) as Record<string, UnreadInfo>);
      };
      publishLatest();
      void cache.refresh(allConvIds).then((result) => {
        if (result === 'error') console.error('[Notification] Failed to load persistent unread:', cache.lastError);
        if (result === 'fetched' || result === 'error') publishLatest();
      });
      return;
    }
    if (!currentUser?.id || !db) return;

    const orderIds = myOrderIdsKey ? myOrderIdsKey.split(',') : [];
    const directIds = directIdsKey ? directIdsKey.split(',') : [];
    const cardIds = cardIdsKey ? cardIdsKey.split(',') : [];
    const allConvIds = [...orderIds, ...directIds, ...cardIds];

    if (allConvIds.length === 0) {
      setPersistentFromMetadata({});
      return;
    }

    const lastReadChat = currentUser.lastReadChat || {};

    // Create a key to avoid re-running unnecessarily
    const loadKey = `${currentUser.id}_${JSON.stringify(lastReadChat)}_${myOrderIdsKey}_${directIdsKey}_${cardIdsKey}`;
    if (loadKey === persistentLoadedRef.current) return;
    persistentLoadedRef.current = loadKey;

    // Batch read conversation metadata docs (Firestore 'in' limit is 10)
    const batches: string[][] = [];
    for (let i = 0; i < allConvIds.length; i += 10) {
      batches.push(allConvIds.slice(i, i + 10));
    }

    Promise.all(batches.map(batch =>
      db!.collection('conversations')
        .where(firebase.firestore.FieldPath.documentId(), 'in', batch)
        .get()
    )).then(snapshots => {
      const unread: Record<string, UnreadInfo> = {};
      for (const snap of snapshots) {
        for (const doc of snap.docs) {
          const data = doc.data();
          const convId = doc.id;
          const convLastRead = lastReadChat[convId] || {};
          const isDirect = convId.startsWith('direct_');
          // Order-Board text threads talk to the customer on the customer
          // channel — that IS the thread, for the staff working the board.
          const isCard = convId.startsWith('card_');
          const channels: string[] = [];

          // Direct chats only use the 'dispatch' channel. Order chats
          // count unread only for channels this viewer is a participant in —
          // strict pair privacy (no observer unread for non-participants).
          //   Admin              → dispatch + admin_store
          //   Driver             → customer + dispatch + driver_store
          //   Store              → admin_store + driver_store
          //   Warehouse Manager  → direct only (no order participation)
          //   Packager           → direct only (no order participation)
          const roleKey = currentUser?.role;
          const channelsToCheck = isDirect
            ? ['dispatch']
            : isCard
            // Board threads have BOTH tabs in use: customer texting and the
            // staff-side DRIVER tab (dispatch channel).
            ? ['customer', 'dispatch']
            : roleKey === UserRole.DRIVER
              ? ['customer', 'dispatch', 'driver_store']
              : roleKey === UserRole.STORE
                ? ['admin_store', 'driver_store']
                : roleKey === UserRole.WAREHOUSE_MANAGER || roleKey === UserRole.WORKER || roleKey === UserRole.PACKAGER
                  ? [] // no order-channel unread for warehouse-side roles
                  : ['dispatch', 'admin_store']; // admin-tier (super_admin / admin / dispatch)

          for (const ch of channelsToCheck) {
            const lastMsgTime = data.lastMessagePerChannel?.[ch] || 0;
            const lastReadTime = convLastRead[ch] || 0;
            const lastSender = data.lastSenderPerChannel?.[ch];
            // Unread if: has a message newer than last read AND it wasn't sent by this user
            if (lastMsgTime > lastReadTime && lastSender !== currentUser!.id) {
              channels.push(ch);
            }
          }

          if (channels.length > 0) {
            const channelCounts: Record<string, number> = {};
            for (const ch of channels) channelCounts[ch] = 1;
            unread[convId] = {
              orderId: convId,
              channel: channels[0],
              channels,
              channelCounts,
              senderName: data.lastSenderName || '',
              lastMessage: data.lastMessageBody || 'Unread messages',
              timestamp: data.lastMessageAt || 0,
              count: channels.length,
            };
          }
        }
      }
      setPersistentFromMetadata(unread);
    }).catch(err => {
      console.error('[Notification] Failed to load persistent unread:', err);
    });
  }, [currentUser?.id, currentUser?.lastReadChat, myOrderIdsKey, directIdsKey, cardIdsKey, setPersistentFromMetadata]);

  // ═══ ORDER ASSIGNMENT NOTIFICATIONS (drivers only) ═══
  useEffect(() => {
    if (!currentUser || currentUser.role !== UserRole.DRIVER) {
      knownOrderIdsRef.current = null;
      return;
    }

    const myAssignedOrders = orders.filter(o =>
      o.driverId === currentUser.id && o.status === OrderStatus.ASSIGNED
    );
    const currentIds = new Set(myAssignedOrders.map(o => o.id));

    if (knownOrderIdsRef.current === null) {
      knownOrderIdsRef.current = currentIds;
      return;
    }

    // Snapshot the driver's "already opened" set. Orders the driver has
    // previously tapped (lastReadOrders[orderId] is set) must not re-fire
    // addUnread on every page refresh — otherwise we resurrect stale "NEW"
    // badges indefinitely. This also defeats the mount race where
    // knownOrderIdsRef initializes with an empty Set before Firestore's
    // first orders snapshot has arrived.
    const alreadySeen = currentUser.lastReadOrders || {};
    const chatRead = currentUser.lastReadChat || {};
    const now = Date.now();

    myAssignedOrders.forEach(order => {
      if (alreadySeen[order.id]) return; // driver has already opened this order
      // Opened its chat (any channel marked read) — also "seen".
      if (chatRead[order.id] && Object.keys(chatRead[order.id]).length > 0) return;
      // The orders list can arrive in more than one batch on a reload (cache,
      // then network, then a paged remainder); an order that was assigned
      // long before this tab opened is not a NEW delivery, whichever batch it
      // rides in — the phone push already announced it at the time.
      const assignedAt = order.assignedAt || order.updatedAt || order.createdAt || 0;
      if (assignedAt && now - assignedAt > NEW_DELIVERY_MAX_AGE_MS) return;
      if (!knownOrderIdsRef.current!.has(order.id)) {
        const orderNum = order.orderNumber || order.id.slice(-6).toUpperCase();
        // Title: order number first (most useful disambiguator), customer name second.
        const title = `🚚 New delivery · #${orderNum}`;
        // Body: the quick-details triple a driver needs to triage — priority,
        // bags, truncated address. Omit blanks so short orders don't read weird.
        // Customer name is admin-only — strip for driver / store / dispatch
        // recipients to match the in-app surface gating.
        const role = currentUserRoleRef.current;
        const seesCustomerName = role === UserRole.ADMIN || isSuperTier(role);
        const parts: string[] = seesCustomerName ? [order.customerName] : [];
        if (order.priority != null && String(order.priority).trim().length > 0) {
          parts.push(String(order.priority).trim());
        }
        if (order.bags != null && order.bags > 0) parts.push(`🛍${order.bags}`);
        if (order.address) {
          const street = order.address.split(',')[0] || order.address;
          parts.push(street.length > 30 ? street.slice(0, 27) + '…' : street);
        }
        const body = parts.join(' · ');
        fireNotification(title, body, { type: 'order_assigned', orderId: order.id }, () => navigate('/driver_app'), '/driver_app');
        triggerFlash();
        // Use 'dispatch' channel, not 'system'. The Messages page clears unread
        // via markChannelRead(orderId, 'dispatch') — if we tag the entry with
        // 'system', the filter in markChannelRead never matches it and the
        // entry hangs around forever no matter how many times the driver taps.
        addUnread({
          orderId: order.id,
          channel: 'dispatch',
          senderName: 'Admin',
          lastMessage: body,
          timestamp: Date.now(),
        });
      }
    });

    knownOrderIdsRef.current = currentIds;
  }, [currentUser?.id, orders]);

  // ═══ MARKED DELIVERED BY THE OFFICE (drivers only, 0105) ═══
  // The office can mark a driver's order delivered (Mark delivered). The driver's phone gets a push; the
  // screen they have open says so too — only for a change seen live in this tab (never on first load),
  // and never for the driver's own delivery (no delivered_by, or their own id).
  const myOrderStatusRef = useRef<Map<string, string> | null>(null);
  useEffect(() => {
    if (!currentUser || currentUser.role !== UserRole.DRIVER) {
      myOrderStatusRef.current = null;
      return;
    }
    const mine = orders.filter(o => o.driverId === currentUser.id);
    const before = myOrderStatusRef.current;
    myOrderStatusRef.current = new Map(mine.map(o => [o.id, String(o.status)]));
    if (before === null) return;
    for (const o of mine) {
      const was = before.get(o.id);
      if (!was || was === OrderStatus.DELIVERED || o.status !== OrderStatus.DELIVERED) continue;
      if (!o.deliveredBy || o.deliveredBy === currentUser.id) continue;
      const orderNum = o.orderNumber || o.id.slice(-6).toUpperCase();
      const who = users.find(u => u.id === o.deliveredBy)?.name || 'The office';
      fireNotification(
        `✓ Marked delivered · #${orderNum}`,
        `${who} marked this delivery as delivered.`,
        { type: 'order_marked_delivered', orderId: o.id },
        () => navigate('/driver_app'),
        '/driver_app',
      );
    }
  }, [currentUser?.id, orders]);

  // ═══ REAL-TIME MESSAGE NOTIFICATIONS ═══
  // Fix A: Listeners only rebuild when actual conversation IDs change.
  // myOrderIdsKey and directIdsKey are string primitives — React compares
  // them by value, so same IDs = no cleanup/rebuild = listeners stay alive.
  // Supabase: the per-message alert logic (the second effect below) reads its
  // inputs through this ref. It is refreshed on exactly the deps the original
  // effect had — so every callback sees the same values it would have closed
  // over — but the shared `messages` subscription itself survives an id-set
  // change instead of being torn down and re-joined (that cost three joins
  // per tab at start, as users, orders and cards arrived one after another,
  // and one more per new order after).
  const msgAlertCtxRef = useRef<{
    idSet: Set<string>; since: number; orders: typeof orders; users: typeof users;
    navigate: typeof navigate; addUnread: typeof addUnread; triggerFlash: typeof triggerFlash;
  } | null>(null);
  const hasWatchedConversations = !!(myOrderIdsKey || directIdsKey || cardIdsKey);
  useEffect(() => {
    if (USE_SUPABASE) {
      if (!currentUserIdRef.current) { msgAlertCtxRef.current = null; return; }
      const orderIds = myOrderIdsKey ? myOrderIdsKey.split(',') : [];
      const directIds = directIdsKey ? directIdsKey.split(',') : [];
      const cardIds = cardIdsKey ? cardIdsKey.split(',') : [];
      const idSet = new Set([...orderIds, ...directIds, ...cardIds]);
      msgAlertCtxRef.current = idSet.size === 0
        ? null
        : { idSet, since: startTimeRef.current, orders, users, navigate, addUnread, triggerFlash };
      return;
    }
    if (!currentUserIdRef.current || !db) return;

    const orderIds = myOrderIdsKey ? myOrderIdsKey.split(',') : [];
    const directIds = directIdsKey ? directIdsKey.split(',') : [];
    const cardIds = cardIdsKey ? cardIdsKey.split(',') : [];
    const allIds = [...orderIds, ...directIds, ...cardIds];

    if (allIds.length === 0) return;

    if (messageUnsubRef.current) {
      messageUnsubRef.current();
      messageUnsubRef.current = null;
    }

    const since = startTimeRef.current;
    const unsubs: (() => void)[] = [];

    // Track last known message timestamps per conversation to detect new messages
    const lastKnownTimestamps: Record<string, number> = {};

    // Batch into groups of 10 (Firestore 'in' query limit)
    const batches: string[][] = [];
    for (let i = 0; i < allIds.length; i += 10) {
      batches.push(allIds.slice(i, i + 10));
    }

    for (const batch of batches) {
      const unsub = db.collection('conversations')
        .where(firebase.firestore.FieldPath.documentId(), 'in', batch)
        .onSnapshot((snap) => {
          snap.docChanges().forEach(change => {
            if (change.type === 'modified' || change.type === 'added') {
              const data = change.doc.data();
              const convId = change.doc.id;
              const lastMsgAt = data.lastMessageAt || 0;
              const lastSender = data.lastSenderId;
              const prevTs = lastKnownTimestamps[convId] || since;

              // Initialize on first load (added), only notify on modifications with newer messages
              if (change.type === 'added') {
                lastKnownTimestamps[convId] = lastMsgAt;
                return;
              }

              // New message arrived from someone else
              if (lastMsgAt > prevTs && lastSender !== currentUserIdRef.current) {
                lastKnownTimestamps[convId] = lastMsgAt;
                const senderName = data.lastSenderName || 'Unknown';
                const body = data.lastMessageBody || 'New message';
                const isDirect = convId.startsWith('direct_');
                const channel = data.lastMessageChannel || 'dispatch';

                // Strict pair filter — match server-side fanout. Skip channels
                // this viewer isn't a participant in so the badge doesn't
                // flag dispatch admins on customer SMS or driver_store traffic
                // they're not actually in. Direct chats always pass.
                const isCard = convId.startsWith('card_');
                if (isCard) {
                  // Board threads: customer texting plus the staff-side
                  // DRIVER tab (dispatch channel) both count.
                  if (channel !== 'customer' && channel !== 'dispatch') return;
                } else if (!isDirect) {
                  const role = currentUserRoleRef.current;
                  // Warehouse Manager + Packager have no order participation,
                  // so no order-channel message ever counts for them.
                  if (role === UserRole.WAREHOUSE_MANAGER || role === UserRole.WORKER || role === UserRole.PACKAGER) return;
                  const allowed: string[] =
                    role === UserRole.DRIVER
                      ? ['customer', 'dispatch', 'driver_store']
                      : role === UserRole.STORE
                        ? ['admin_store', 'driver_store']
                        : ['dispatch', 'admin_store']; // admin-tier
                  if (!allowed.includes(channel)) return;
                }

                // Look up the order for context in the title (order#, customer).
                // Customer name is admin-only — drivers / stores / dispatch see
                // only the order number to mirror in-app gating.
                const order = !isDirect ? orders.find(o => o.id === convId) : null;
                const orderNum = order?.orderNumber || convId.slice(-6).toUpperCase();
                const role = currentUserRoleRef.current;
                const seesCustomerName = role === UserRole.ADMIN || isSuperTier(role);
                const customer = seesCustomerName ? (order?.customerName || '') : '';

                const title = isDirect
                  ? `💬 ${senderName}`
                  : channel === 'customer'
                    ? `📱 SMS · #${orderNum}${customer ? ` · ${customer}` : ''}`
                    : `💬 #${orderNum}${customer ? ` · ${customer}` : ''}`;
                // Body: always lead with the sender name so the recipient knows
                // who just pinged them, even when the title is order-centric.
                const notifBody = isDirect ? body : `${senderName}: ${body}`;

                // Routing data — click handler in the SW and the in-tab fallback
                // both use this to deep-link into the right chat + channel.
                const notifData: NotificationData = isDirect
                  ? {
                      type: 'direct_message',
                      conversationId: convId,
                      fromUserId: data.lastSenderId || undefined,
                    }
                  : { type: 'new_message', orderId: convId, channel };

                const targetUrl = buildNotificationUrl(notifData, currentUserRoleRef.current || undefined);
                fireNotification(title, notifBody, notifData, () => navigate(targetUrl), targetUrl);
                triggerFlash();
                addUnread({
                  orderId: convId,
                  channel,
                  senderName,
                  lastMessage: body,
                  timestamp: lastMsgAt,
                });
              } else {
                lastKnownTimestamps[convId] = lastMsgAt;
              }
            }
          });
        }, (err) => {
          console.error('[Notification] Firestore batch listener error:', err);
        });
      unsubs.push(unsub);
    }

    messageUnsubRef.current = () => unsubs.forEach(u => u());

    return () => {
      if (messageUnsubRef.current) {
        messageUnsubRef.current();
        messageUnsubRef.current = null;
      }
    };
  }, [myOrderIdsKey, directIdsKey, cardIdsKey, addUnread, triggerFlash]);

  // The subscription itself: opened once per signed-in user while there is at
  // least one conversation to watch, on the tab's shared `messages` channel.
  // Only for the roles with a Chat page (services/liveTablePolicy): the
  // packaging-house roles have none — their order-chat alerts were already
  // skipped above, and a message they cannot open is not worth a policy check
  // per message per tab.
  const chatRole = roleHasChat(currentUser?.role);
  useEffect(() => {
    if (!USE_SUPABASE || !currentUser?.id || !chatRole || !hasWatchedConversations) return;
    // `messages` INSERTs replace the batched conversation-doc listeners — the
    // payload carries the full new row, so the alert logic runs per message
    // with the same strict-pair filters. The binding itself is the tab's ONE
    // shared `messages` channel (services/tableHub): CustomerReplyAlerts and
    // every open chat ride it too, and the hub hands this consumer only the
    // events it asks for below.
    const onInsert = (payload: any) => {
        const ctx = msgAlertCtxRef.current;
        if (!ctx) return;
        const { idSet, since, orders, navigate, addUnread, triggerFlash } = ctx;
        const row = payload?.new;
        if (!row) return;
        const convId = row.conversation_id as string;
        if (!idSet.has(convId)) return;
        // Its stored meta just changed: the persistent-unread cache fetches this thread again.
        metaCacheRef.current?.invalidate(convId);
        const lastMsgAt = msFromISO0(row.created_at);
        const lastSender = row.sender_id || '';
        if (!(lastMsgAt > since) || lastSender === currentUserIdRef.current) return;
        const senderName = row.sender_name || 'Unknown';
        const body = row.body || 'New message';
        const isDirect = convId.startsWith('direct_');
        const channel = row.channel || 'dispatch';

        // Strict pair filter — match server-side fanout (same as Firestore path).
        const isCard = convId.startsWith('card_');
        if (isCard) {
          // Board threads: customer texting plus the staff-side
          // DRIVER tab (dispatch channel) both count.
          if (channel !== 'customer' && channel !== 'dispatch') return;
        } else if (!isDirect) {
          const role = currentUserRoleRef.current;
          if (role === UserRole.WAREHOUSE_MANAGER || role === UserRole.WORKER || role === UserRole.PACKAGER) return;
          const allowed: string[] =
            role === UserRole.DRIVER
              ? ['customer', 'dispatch', 'driver_store']
              : role === UserRole.STORE
                ? ['admin_store', 'driver_store']
                : ['dispatch', 'admin_store']; // admin-tier
          if (!allowed.includes(channel)) return;
        }

        const order = !isDirect ? orders.find(o => o.id === convId) : null;
        const orderNum = order?.orderNumber || convId.slice(-6).toUpperCase();
        const role = currentUserRoleRef.current;
        const seesCustomerName = role === UserRole.ADMIN || isSuperTier(role);
        const customer = seesCustomerName ? (order?.customerName || '') : '';

        const title = isDirect
          ? `💬 ${senderName}`
          : channel === 'customer'
            ? `📱 SMS · #${orderNum}${customer ? ` · ${customer}` : ''}`
            : `💬 #${orderNum}${customer ? ` · ${customer}` : ''}`;
        const notifBody = isDirect ? body : `${senderName}: ${body}`;

        const notifData: NotificationData = isDirect
          ? {
              type: 'direct_message',
              conversationId: convId,
              fromUserId: row.sender_id || undefined,
            }
          : { type: 'new_message', orderId: convId, channel };

        const targetUrl = buildNotificationUrl(notifData, currentUserRoleRef.current || undefined);
        fireNotification(title, notifBody, notifData, () => navigate(targetUrl), targetUrl);
        triggerFlash();
        addUnread({
          orderId: convId,
          channel,
          senderName,
          lastMessage: body,
          timestamp: lastMsgAt,
        });
    };

    // ── parity: reaction on MY message → same alert + sound ───────────────
    // `messages` carries REPLICA IDENTITY FULL (0025), so the payload has
    // `old` and the diff is reliable. Everything that decides whether to fire
    // is in services/reactionAlert.ts.
    //
    // UPDATEs are ASKED FOR ONLY UNDER THE FLAG (the `events` list below),
    // not merely short-circuited inside the callback: with the flag off this
    // consumer never sees an UPDATE at all, exactly as when its own binding
    // was INSERT-only. (The tab's shared channel is bound to `*` — one
    // server-side subscription per table per tab is the whole point of the
    // hub — and the hub filters per consumer, client-side.)
    const onReactionUpdate = (payload: any) => {
        const ctx = msgAlertCtxRef.current;
        if (!ctx) return;
        const { idSet, orders, users, navigate, addUnread, triggerFlash } = ctx;
        const row = payload?.new;
        if (!row) return;
        const convId = row.conversation_id as string;
        if (!idSet.has(convId)) return;
        const decision = decideReactionAlert({
          before: payload?.old?.reactions,
          beforeKnown: !!payload?.old,
          after: row.reactions,
          message: { senderId: row.sender_id, body: row.body, channel: row.channel },
          viewerId: currentUserIdRef.current,
          nameOf: (id) => users.find((u) => u.id === id)?.name,
        });
        if (!decision) return;
        const isDirect = convId.startsWith('direct_');
        const order = !isDirect ? orders.find((o) => o.id === convId) : null;
        const orderNum = order?.orderNumber || convId.slice(-6).toUpperCase();
        const channel = row.channel || 'dispatch';
        const notifData: NotificationData = isDirect
          ? { type: 'direct_message', conversationId: convId, fromUserId: decision.actorId }
          : { type: 'new_message', orderId: convId, channel };
        const targetUrl = buildNotificationUrl(notifData, currentUserRoleRef.current || undefined);
        fireNotification(
          isDirect ? `💬 ${decision.actorName}` : `💬 #${orderNum}`,
          decision.text,
          notifData,
          () => navigate(targetUrl),
          targetUrl,
        );
        triggerFlash();
        // The reaction also bumped conversations.meta (contract 5b), so the
        // thread IS unread on the next load — say so now rather than have the
        // badge appear out of nowhere on refresh.
        addUnread({
          orderId: convId,
          channel,
          senderName: decision.actorName,
          lastMessage: reactionPreviewBody(decision.emoji, previewSnippet(row.body)),
          timestamp: Date.now(),
        });
    };
    const leave = subscribeTableHub('messages', {
      events: FEATURES.chatParity ? ['INSERT', 'UPDATE'] : ['INSERT'],
      // The hub hands this consumer only the events listed above, so an
      // UPDATE arrives only under chatParity.
      onChange: (payload: any) => {
        if (payload?.eventType === 'UPDATE') onReactionUpdate(payload);
        else onInsert(payload);
      },
    });
    return () => { leave(); };
  }, [currentUser?.id, chatRole, hasWatchedConversations]);

  return null;
};
